import { Property, Type, TypeManager } from '@dipscope/type-manager';
import PlayerList from '../../core/Playerlist';
import { ActionDTO, AgentSettingsDTO, CreateMessageDTO, GameConfigDTO } from '@envclient/envcore';
import logger from '../../logger';
import { gameConfigs, tileCountConfig } from './kGameConfigLoader';
import KTileLogic from './KTileLogic';
import KPlayer, { KPlayerDTO } from './KPlayer';
import { Env, EnvType } from '../../core/Env';
import { TileCountMap, TileDef, TileGameConfig } from '../tilebase/TileGameConfig';
import { TileDTO } from '../tilebase/Tile';
import { Client } from '../../core/WsApp';
import { Agent } from '../../core/Agent';
import { MeepleType } from '../tilebase/Meeple';
import { KTileDef } from './KTileGameConfig';
import { KActionDTO, KResourceActionDTO, KSubmitActionDTO, KTaxActionDTO, Place } from './KTile';
import 'reflect-metadata';

export interface KEnvDTO {
  actPlayer?: KPlayerDTO;
  players: KPlayerDTO[];
  gameType: string;
  envType: string;
  gameConfig: GameConfigDTO;
  clone?: { [key: string]: unknown };
  currentActions: KActionDTO[];
}

export type TurnType = 'tile' | 'resource' | 'taxman';
@Type({ discriminant: 'KEnv', injectable: true })
class KEnv implements Env {
  // Server-only bot agent factory. KEnv is isomorphic (also bundled by the frontend), so it
  // must NOT import the agent (which pulls in Node's worker_threads). The server registers a
  // factory at startup (see index.ts); the frontend leaves it null and never creates agents.
  static agentFactory: ((playerId: number) => Agent) | null = null;

  @Property()
  envType: EnvType;
  @Property()
  gameType: string;
  @Property([KPlayer])
  playerList: PlayerList<KPlayer>;

  //  game config
  gameConfig: TileGameConfig;
  tileCountConfig: TileCountMap;

  @Property()
  tileLogic: KTileLogic;

  maxPlayers: KPlayer[];

  init(createMessage: CreateMessageDTO) {
    logger.info('new KEnv');

    this.gameType = createMessage.gameType as string;

    this.envType = 'koenigsburg' as const;

    this.playerList = new PlayerList();
    this.playerList.playerType = KPlayer;

    this.gameConfig = gameConfigs.find((gf) => gf.type === this.gameType);
    this.tileCountConfig = tileCountConfig;

    this.tileLogic = new KTileLogic();
    this.tileLogic.init(this.gameConfig, tileCountConfig, this.playerList);
  }

  calculateBestAction(settings?: AgentSettingsDTO): Promise<ActionDTO> {
    if (!this.playerList.actPlayer.agent) return Promise.resolve(null);

    // calc best action on new thread - then send it to client for visualisation purposes
    // TODO dont clone it every time
    return this.playerList.actPlayer.agent.calculateBestAction(this, settings);
  }

  consumeGameEvents() {
    const recoveredMeeples = this.tileLogic.recoveredMeeples.map((m) => m.toDTO());
    this.tileLogic.recoveredMeeples = [];
    if (recoveredMeeples.length > 0) {
      return [
        {
          type: 'RECOVERED_MEEPLES',
          recoveredMeeples
        }
      ];
    }
    return [];
  }

  gameInfo() {
    return this.tilesLeft() + ' tiles left';
  }

  players() {
    return this.playerList.players;
  }

  playerById(id: number) {
    return this.playerList.playerById(id);
  }

  addPlayer(client: Client) {
    const player: KPlayer = this.playerList.addPlayer(client.user) as KPlayer;
    player.createMeeples(new Map(Object.entries(this.gameConfig.meepleCount)) as Map<MeepleType, number>);

    // bot players get an agent (created via the server-registered factory; null on frontend)
    if (client.user.type === 'bot' && KEnv.agentFactory) {
      player.agent = KEnv.agentFactory(player.id);
    }

    // add hexa tiles into player.asset
    player.assets = this.tileLogic.createAssetTiles();

    return player;
  }

  setAgentSettings(player: KPlayer, settings: AgentSettingsDTO) {
    player.agentSettings = settings;
    if (settings.useAgent && !player.agent && KEnv.agentFactory) {
      player.agent = KEnv.agentFactory(player.id);
    } else if (!settings.useAgent && player.agent) {
      player.agent.terminateAll();
      player.agent = null;
    }
  }

  removePlayer(clientId: number) {
    const player = this.playerList.removePlayer(clientId);

    this.tileLogic.removePlayerMeeples(clientId);

    return player;
  }

  actPlayer() {
    return this.playerList.actPlayer;
  }

  tilesLeft(): number {
    return this.tileLogic.tilesLeft();
  }

  data(): KEnvDTO {
    // UNLIKE CcsonEnv, this is NOT optional/bot-only here: KTileMap.tsx deserializes clone into the
    // authoritative local `kEnv` instance that ALL koenigsburg rendering depends on (tiles, actions,
    // everything - see its `useEffect(() => { if (clone) { ... setKEnv(k) } }, [clone])`), for every
    // game, agent or not. Gating this the way CcsonEnv.data() does broke board rendering entirely
    // for any koenigsburg game without a client-compute bot - confirmed and reverted 2026-09-02.
    const clone = TypeManager.serialize(KEnv, this);
    return {
      envType: this.envType,
      gameType: this.gameType,
      players: this.players().map((p) => p.toDTO()),
      actPlayer: this.playerList.actPlayer ? this.playerList.actPlayer.toDTO() : null,
      gameConfig: this.gameConfig,
      currentActions: this.tileLogic.currentActions,
      clone
    };
  }

  step(action: KSubmitActionDTO | KResourceActionDTO | KTaxActionDTO | null) {
    if (action == null) {
      this.tileLogic.skipTile();
    } else if ('tiles' in action) {
      this.tileLogic.applyAction(action as KSubmitActionDTO);
    } else if ('resources' in action) {
      this.tileLogic.applyResourceAction(action as KResourceActionDTO);
    } else if ('taxNodes' in action) {
      this.tileLogic.applyTaxAction(action as KTaxActionDTO);
    } else {
      return;
    }
    this.actPlayer().inactiveCount = 0;
  }

  next(): boolean {
    if (this.playerList.players.length == 0) return false;
    if (this.tileLogic.tilesLeft() == 0) return false;
    // resource turn
    if (this.tileLogic.turn === 'resource') {
      //
    } else if (this.tileLogic.turn === 'taxman') {
      //
    } else {
      // normal turn: tile turn for the next player
      this.playerList.nextPlayer();
      this.tileLogic.pullTiles();
    }
    return true;
  }

  hasEnded(): boolean {
    // wait until final resources are harvested if any
    return (
      this.tileLogic.tilesLeft() == 0 &&
      this.tileLogic.lastTiles.length == 0 &&
      Array.from(this.tileLogic.harvestableResources.map.keys()).length === 0
    );
  }

  state() {
    // TODO
    return {};
  }

  currentActions(): KActionDTO[] {
    return this.tileLogic.currentActions;
  }

  /**
   * Calculates final scores, does the final update on tiles.
   * Calculates max score and winners.
   *
   */
  endGame() {
    logger.info('Game.endGame');

    this.tileLogic.finalUpdate();

    // calculate maxScore and winners
    this.players().forEach((player) => {
      logger.info('Player score: Name({}). Score({}).', player.displayName, player.score);
    });

    //find winner(s)
    this.maxPlayers = [];
    const maxScore = Math.max(...this.players().map((p) => p.score));

    this.players().forEach((player) => {
      if (player.score === maxScore) this.maxPlayers.push(player);
    });
  }

  winners(): KPlayer[] {
    return this.maxPlayers;
  }

  increaseTimeout() {
    this.tileLogic.skipTile();
    if (this.actPlayer() != null) {
      this.actPlayer().inactiveCount = this.actPlayer().inactiveCount + 1;
      if (this.actPlayer().inactiveCount === 3) {
        return true;
      }
      return false;
    }
    return false;
  }

  gameLabel() {
    return this.gameConfig.label;
  }

  cloneEnv(): KEnv {
    const serialzedEnv = TypeManager.serialize(KEnv, this);
    const clone = TypeManager.deserialize(KEnv, serialzedEnv);
    return clone;
  }
}

export default KEnv;
